import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { TextInput } from 'components/common/form/common';
import { EditorBlockTextField } from 'components/editor/blocks';

interface Props {
  index: number;
  link: { time: string; title: string };
  onDelete: (index: number, subIndex: number) => void;
  onTimeChange: (value: string, index: number, subIndex: number) => void;
  onTitleChange: (value: string, index: number, subIndex: number) => void;
  subIndex?: number;
  translationOnly: boolean;
}

const validateTime = (value: string) => {
  if (value && value.match(/^[0-9]?[0-9][0-9]:[0-5][0-9]$/)) return undefined;
  return 'error';
};

const EditorBlockVideoLink = ({
  index,
  link,
  onDelete,
  onTimeChange,
  onTitleChange,
  subIndex,
  translationOnly
}: Props) => {
  const { messages, theme } = useReduxState();

  const [localTime, setLocalTime] = useState(link.time || '');
  const [timeError, setTimeError] = useState<string | undefined>(
    validateTime(link.time)
  );

  const updateTimeTimeout = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    return () => clearTimeout(updateTimeTimeout.current);
  }, []);

  // Sync local state when props change externally (AI translations, remote updates, discards)
  useEffect(() => {
    setLocalTime(link.time || '');
    setTimeError(validateTime(link.time));
  }, [link.time]);

  const handleDelete = () => onDelete(index, subIndex);

  const handleTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    setLocalTime(value);

    const error = validateTime(value);
    setTimeError(error);

    if (!error) {
      clearTimeout(updateTimeTimeout.current);
      updateTimeTimeout.current = setTimeout(() => {
        onTimeChange(value, index, subIndex);
      }, 500);
    }
  };

  const handleTitleChange = (value: string) => {
    onTitleChange(value, index, subIndex);
  };

  return (
    <div className="link">
      <div className="time" style={{ width: timeError ? 85 : 58 }}>
        <div style={{ position: 'relative' }}>
          <TextInput
            disabled={translationOnly}
            error={timeError}
            onChange={handleTimeChange}
            placeholder="mm:ss"
            style={{ paddingRight: timeError ? 30 : 10 }}
            value={localTime}
          />
          {timeError ? (
            <div className="error-icon">
              <Icon color={theme.dangerColor} icon="exclamation-circle" />
            </div>
          ) : null}
        </div>
      </div>

      <div className="title">
        <EditorBlockTextField
          onValueChange={handleTitleChange}
          placeholder={messages.commonAddText}
          value={link.title || ''}
        />
      </div>

      {!translationOnly ? (
        <div className="delete">
          <Button
            category="light"
            data-tooltip={messages.commonDelete}
            icon="trash-alt"
            onClick={handleDelete}
            size="small"
          />
        </div>
      ) : null}

      <style jsx>{`
        .link {
          align-items: center;
          display: flex;
        }
        .time {
          flex-grow: 0;
          flex-shrink: 0;
          margin-right: 10px;
        }
        .title {
          flex-grow: 1;
          flex-shrink: 1;
          margin-right: 10px;
        }
        .error-icon {
          line-height: 0;
          position: absolute;
          right: 12px;
          top: 9px;
        }
        .delete {
          flex-grow: 0;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockVideoLink;
